import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {ColorName} from '@github-ui/use-named-color'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Label, Token} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'
import {graphql, readInlineData, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitClearProjectItemFieldValueMutation} from '../../../mutations/clear-project-item-field-value'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import type {SingleSelectFieldConfigFragment$key} from './__generated__/SingleSelectFieldConfigFragment.graphql'
import type {SingleSelectFieldFragment$key} from './__generated__/SingleSelectFieldFragment.graphql'
import type {
  SingleSelectFieldOptionFragment$data,
  SingleSelectFieldOptionFragment$key,
} from './__generated__/SingleSelectFieldOptionFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import {type BaseFieldProps, getTokenStyle} from './Shared'
import styles from './SingleSelectField.module.css'
import {SingleSelectLeadingVisual, SingleSelectToken} from './SingleSelectToken'

const AUTO_ASSIGN_STATUS_LABEL = 'Auto-assign to Agent'
const EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS = new Set<string>()

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const SingleSelectFieldFragment = graphql`
  fragment SingleSelectFieldFragment on ProjectV2ItemFieldSingleSelectValue {
    id
    optionId
    name
    nameHTML
    color
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const SingleSelectFieldOptionFragment = graphql`
  fragment SingleSelectFieldOptionFragment on ProjectV2SingleSelectFieldOption @inline {
    id
    optionId
    name
    nameHTML
    color
    descriptionHTML
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const SingleSelectFieldConfigFragment = graphql`
  fragment SingleSelectFieldConfigFragment on ProjectV2SingleSelectField {
    id
    name
    options {
      ...SingleSelectFieldOptionFragment
      # The status field has a non unique id. We leverage the getDataID method in the Relay environment, and in this
      # instance use the id, name, description and color to generate a unique ID:
      # - https://github.com/github/github/pull/305841/files#diff-7187f5ed804646bc353b5176a059d44bcd6a4cd5d5f19976aebb885d1e6997b8R155
      # eslint-disable-next-line relay/unused-fields
      id
      # eslint-disable-next-line relay/unused-fields
      name
      # eslint-disable-next-line relay/unused-fields
      description
      # eslint-disable-next-line relay/unused-fields
      color
    }
  }
`

type SingleSelectFieldProps = BaseFieldProps<SingleSelectFieldConfigFragment$key, SingleSelectFieldFragment$key> & {
  autoAssignWorkflowOptionIds?: ReadonlySet<string>
}

export function SingleSelectField({
  viewerCanUpdate,
  itemId,
  projectId,
  field,
  value,
  onIssueUpdate,
  isStatusField,
  autoAssignWorkflowOptionIds = EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS,
}: SingleSelectFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const environment = useRelayEnvironment()

  const fieldData = useFragment(SingleSelectFieldConfigFragment, field)
  const valueData = useFragment(SingleSelectFieldFragment, value)

  const fieldId = fieldData.id
  const fieldName = fieldData.name

  const [filter, setFilter] = useState('')

  const onSelectionChange = useCallback(
    (selection: SingleSelectFieldOptionFragment$data[]) => {
      if (selection.length === 1) {
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            value: {singleSelectOptionId: selection[0]!.optionId},
          },
          onCompleted: onIssueUpdate,
        })
      } else if (selection.length === 0) {
        commitClearProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
          },
          onCompleted: onIssueUpdate,
        })
      }
      setShowInput(false)
    },
    [environment, fieldId, itemId, onIssueUpdate, projectId],
  )

  const filterItems = useCallback((v: string) => {
    setFilter(v)
  }, [])

  const items = useMemo(() => {
    const options = fieldData.options || []
    const optionsData = options.map(i =>
      // eslint-disable-next-line no-restricted-syntax
      readInlineData<SingleSelectFieldOptionFragment$key>(SingleSelectFieldOptionFragment, i),
    )
    if (!filter) return optionsData
    return optionsData.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fieldData.options, filter])

  const selectedItems = useMemo(() => {
    const options = fieldData.options || []
    const optionsData = options.map(i =>
      // eslint-disable-next-line no-restricted-syntax
      readInlineData<SingleSelectFieldOptionFragment$key>(SingleSelectFieldOptionFragment, i),
    )
    return optionsData.filter(i => i.optionId === valueData?.optionId)
  }, [fieldData.options, valueData?.optionId])

  const itemPickerCacheKey = useMemo(() => {
    return isStatusField ? Array.from(autoAssignWorkflowOptionIds).sort().join(',') : ''
  }, [autoAssignWorkflowOptionIds, isStatusField])

  const convertToItemProps = useCallback(
    (option: SingleSelectFieldOptionFragment$data): ExtendedItemProps<SingleSelectFieldOptionFragment$data> => {
      return {
        id: option.optionId,
        children: (
          <div className={styles.optionContent}>
            <span className={styles.optionTitle}>
              <span className={styles.optionName}>
                <VerifiedHTMLText
                  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
                  html={option.nameHTML as SafeHTMLString}
                />
              </span>
              {isStatusField && autoAssignWorkflowOptionIds.has(option.optionId) && (
                <Label className={styles.autoAssignLabel}>{AUTO_ASSIGN_STATUS_LABEL}</Label>
              )}
            </span>
            <VerifiedHTMLText
              // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
              html={option.descriptionHTML as SafeHTMLString}
              className={styles.VerifiedHTMLText}
            />
          </div>
        ),
        source: option,
        leadingVisual: () => <SingleSelectLeadingVisual inputColor={option.color as ColorName} />,
      }
    },
    [autoAssignWorkflowOptionIds, isStatusField],
  )

  const valueDataColor = valueData?.color
  const valueDataName = valueData?.name

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      return (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          name={fieldName}
          isStatusField={isStatusField}
          placeholder={
            isStatusField ? (
              <div className={styles.statusPlaceholder}>
                {LABELS.emptySections.status}
                <TriangleDownIcon />
              </div>
            ) : (
              <>{LABELS.emptySections.singleSelectFilterPlaceholder}</>
            )
          }
          value={
            valueDataName && valueDataColor ? (
              <div className={styles.selectedValueContainer}>
                <SingleSelectToken inputName={valueDataName} inputColor={valueDataColor as ColorName} />
                {isStatusField && <TriangleDownIcon />}
              </div>
            ) : null
          }
          anchorProps={anchorProps}
        />
      )
    },
    [fieldName, isStatusField, showInput, valueDataColor, valueDataName],
  )

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        canUpdate={false}
        name={fieldName}
        placeholder={LABELS.emptySections.noValue(fieldName)}
        value={
          valueData?.name ? (
            <Token className={getTokenStyle(valueData.name)} size="medium" text={valueData.name} />
          ) : null
        }
        isStatusField={isStatusField}
      />
    )

  return (
    <ItemPicker
      key={itemPickerCacheKey}
      items={items}
      initialSelectedItems={selectedItems}
      filterItems={filterItems}
      getItemKey={i => i.optionId}
      convertToItemProps={convertToItemProps}
      placeholderText={LABELS.emptySections.singleSelectFilterPlaceholder}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={renderAnchor}
      height={'large'}
    />
  )
}
